import { isSalesOrderHubPendingStatus, salesOrderStatusLabel } from "../../lib/salesOrderHubStatus";

export type InsideSalesSalesOrderReportSortKey =
  | "number"
  | "customerPurchaseOrderNumber"
  | "clientName"
  | "endUserName"
  | "submitted"
  | "shipByDate"
  | "model"
  | "quantity"
  | "unitPrice"
  | "lineValue"
  | "repAgencyName"
  | "shippingState"
  | "ownerName";

export type InsideSalesSalesOrderReportRow = {
  id: number;
  salesOrderId: number;
  number: string;
  status: string | null;
  submitted: string | null;
  shipByDate: string | null;
  customerPurchaseOrderNumber: string | null;
  clientName: string | null;
  endUserName: string | null;
  repAgencyName: string | null;
  shippingState: string | null;
  ownerName: string | null;
  model: string | null;
  quantity: number;
  unitPrice: number;
  lineValue: number;
};

export type InsideSalesReportFilterOperator =
  | "eq"
  | "neq"
  | "contains"
  | "not_contains"
  | "between"
  | "before"
  | "after"
  | "lt"
  | "gt";
export type InsideSalesReportFilterColumnKind = "text" | "date" | "number";

export type InsideSalesReportColumnFilter = {
  id: string;
  column: InsideSalesSalesOrderReportSortKey;
  operator: InsideSalesReportFilterOperator;
  value: string;
  valueTo: string;
};

export const INSIDE_SALES_REPORT_FILTER_COLUMNS: Array<{
  key: InsideSalesSalesOrderReportSortKey;
  label: string;
  kind: InsideSalesReportFilterColumnKind;
}> = [
  { key: "number", label: "SO #", kind: "text" },
  { key: "customerPurchaseOrderNumber", label: "PO #", kind: "text" },
  { key: "clientName", label: "Client", kind: "text" },
  { key: "endUserName", label: "End user", kind: "text" },
  { key: "submitted", label: "SO date", kind: "date" },
  { key: "shipByDate", label: "Ship date", kind: "date" },
  { key: "model", label: "Model", kind: "text" },
  { key: "quantity", label: "Qty", kind: "number" },
  { key: "unitPrice", label: "Unit price", kind: "number" },
  { key: "lineValue", label: "Value", kind: "number" },
  { key: "repAgencyName", label: "Rep agency", kind: "text" },
  { key: "shippingState", label: "Ship state", kind: "text" },
  { key: "ownerName", label: "Owner", kind: "text" },
];

export const INSIDE_SALES_REPORT_FILTER_OPERATORS: Array<{
  key: InsideSalesReportFilterOperator;
  label: string;
}> = [
  { key: "eq", label: "Equal" },
  { key: "neq", label: "Not equal" },
  { key: "contains", label: "Contain" },
  { key: "not_contains", label: "Not contain" },
  { key: "before", label: "Before" },
  { key: "after", label: "After" },
  { key: "lt", label: "Less than" },
  { key: "gt", label: "Greater than" },
  { key: "between", label: "Between" },
];

const NUMERIC_SORT_KEYS = new Set<InsideSalesSalesOrderReportSortKey>(["quantity", "unitPrice", "lineValue"]);

export function createInsideSalesReportColumnFilter(
  overrides: Partial<InsideSalesReportColumnFilter> = {},
): InsideSalesReportColumnFilter {
  return {
    id: overrides.id ?? `filter-${Math.random().toString(36).slice(2, 10)}`,
    column: overrides.column ?? "clientName",
    operator: overrides.operator ?? "contains",
    value: overrides.value ?? "",
    valueTo: overrides.valueTo ?? "",
  };
}

export function reportFilterColumnKind(
  column: InsideSalesSalesOrderReportSortKey,
): InsideSalesReportFilterColumnKind {
  return INSIDE_SALES_REPORT_FILTER_COLUMNS.find((entry) => entry.key === column)?.kind ?? "text";
}

export function operatorsForReportFilterColumn(
  column: InsideSalesSalesOrderReportSortKey,
): Array<{ key: InsideSalesReportFilterOperator; label: string }> {
  const kind = reportFilterColumnKind(column);
  if (kind === "text") {
    return INSIDE_SALES_REPORT_FILTER_OPERATORS.filter(
      (entry) =>
        entry.key !== "between" &&
        entry.key !== "before" &&
        entry.key !== "after" &&
        entry.key !== "lt" &&
        entry.key !== "gt",
    );
  }
  if (kind === "number") {
    return INSIDE_SALES_REPORT_FILTER_OPERATORS.filter(
      (entry) => entry.key !== "before" && entry.key !== "after",
    );
  }
  return INSIDE_SALES_REPORT_FILTER_OPERATORS.filter(
    (entry) => entry.key !== "lt" && entry.key !== "gt",
  );
}

function normalizeReportFilterOperator(
  column: InsideSalesSalesOrderReportSortKey,
  operator: InsideSalesReportFilterOperator,
): InsideSalesReportFilterOperator {
  if (reportFilterColumnKind(column) === "number") {
    if (operator === "before") {
      return "lt";
    }
    if (operator === "after") {
      return "gt";
    }
  }
  if (reportFilterColumnKind(column) === "date") {
    if (operator === "lt") {
      return "before";
    }
    if (operator === "gt") {
      return "after";
    }
  }
  return operator;
}

export function coerceReportFilterOperator(
  column: InsideSalesSalesOrderReportSortKey,
  operator: InsideSalesReportFilterOperator,
): InsideSalesReportFilterOperator {
  const normalized = normalizeReportFilterOperator(column, operator);
  const allowed = operatorsForReportFilterColumn(column);
  return allowed.some((entry) => entry.key === normalized) ? normalized : "eq";
}

export function formatInsideSalesReportColumnFilterChip(filter: InsideSalesReportColumnFilter): string {
  const columnLabel = INSIDE_SALES_REPORT_FILTER_COLUMNS.find((entry) => entry.key === filter.column)?.label ?? filter.column;
  const operator = coerceReportFilterOperator(filter.column, filter.operator);
  const operatorLabel =
    INSIDE_SALES_REPORT_FILTER_OPERATORS.find((entry) => entry.key === operator)?.label.toLowerCase() ?? operator;
  if (operator === "between") {
    const from = formatChipFilterValue(filter.column, filter.value) || "…";
    const to = formatChipFilterValue(filter.column, filter.valueTo) || "…";
    return `${columnLabel} ${operatorLabel} ${from}–${to}`;
  }
  return `${columnLabel} ${operatorLabel} ${formatChipFilterValue(filter.column, filter.value)}`;
}

function formatChipFilterValue(column: InsideSalesSalesOrderReportSortKey, raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return "";
  }
  if (reportFilterColumnKind(column) === "date") {
    return formatReportDate(trimmed) ?? trimmed;
  }
  return trimmed;
}

function haystack(row: InsideSalesSalesOrderReportRow): string {
  return [
    row.number,
    row.customerPurchaseOrderNumber ?? "",
    row.clientName ?? "",
    row.endUserName ?? "",
    row.model ?? "",
    row.repAgencyName ?? "",
    row.shippingState ?? "",
    row.ownerName ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

function parseFilterNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function isoDate(value: string | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  const normalized = value.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null;
}

function textCell(row: InsideSalesSalesOrderReportRow, column: InsideSalesSalesOrderReportSortKey): string {
  const value = row[column];
  if (typeof value === "number") {
    return String(value);
  }
  return String(value ?? "").trim();
}

function numberCell(row: InsideSalesSalesOrderReportRow, column: InsideSalesSalesOrderReportSortKey): number | null {
  const value = row[column];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    return parseFilterNumber(value);
  }
  return null;
}

export function reportColumnFilterIsActive(filter: InsideSalesReportColumnFilter): boolean {
  const operator = coerceReportFilterOperator(filter.column, filter.operator);
  if (operator === "between") {
    return filter.value.trim() !== "" || filter.valueTo.trim() !== "";
  }
  return filter.value.trim() !== "";
}

function matchesTextFilter(
  cell: string,
  operator: InsideSalesReportFilterOperator,
  value: string,
): boolean {
  const left = cell.toLowerCase();
  const right = value.trim().toLowerCase();
  if (operator === "neq") {
    return left !== right;
  }
  if (operator === "contains") {
    return left.includes(right);
  }
  if (operator === "not_contains") {
    return !left.includes(right);
  }
  return left === right;
}

function matchesNumberFilter(
  cell: number | null,
  operator: InsideSalesReportFilterOperator,
  value: string,
  valueTo: string,
): boolean {
  if (operator === "contains") {
    if (cell == null) {
      return false;
    }
    return String(cell).toLowerCase().includes(value.trim().toLowerCase());
  }
  if (operator === "not_contains") {
    if (cell == null) {
      return true;
    }
    return !String(cell).toLowerCase().includes(value.trim().toLowerCase());
  }
  if (cell == null) {
    return operator === "neq";
  }
  if (operator === "between") {
    const from = parseFilterNumber(value);
    const to = parseFilterNumber(valueTo);
    if (from == null && to == null) {
      return true;
    }
    if (from != null && cell < from) {
      return false;
    }
    if (to != null && cell > to) {
      return false;
    }
    return true;
  }
  const compared = parseFilterNumber(value);
  if (compared == null) {
    return true;
  }
  if (operator === "before" || operator === "lt") {
    return cell < compared;
  }
  if (operator === "after" || operator === "gt") {
    return cell > compared;
  }
  if (operator === "neq") {
    return cell !== compared;
  }
  return cell === compared;
}

function matchesDateFilter(
  cell: string | null,
  operator: InsideSalesReportFilterOperator,
  value: string,
  valueTo: string,
): boolean {
  if (operator === "contains") {
    if (cell == null) {
      return false;
    }
    return cell.toLowerCase().includes(value.trim().toLowerCase());
  }
  if (operator === "not_contains") {
    if (cell == null) {
      return true;
    }
    return !cell.toLowerCase().includes(value.trim().toLowerCase());
  }
  if (operator === "between") {
    if (cell == null) {
      return false;
    }
    const from = isoDate(value);
    const to = isoDate(valueTo);
    if (from == null && to == null) {
      return true;
    }
    if (from != null && cell < from) {
      return false;
    }
    if (to != null && cell > to) {
      return false;
    }
    return true;
  }
  const compared = isoDate(value);
  if (compared == null) {
    return true;
  }
  if (cell == null) {
    return operator === "neq";
  }
  if (operator === "before") {
    return cell < compared;
  }
  if (operator === "after") {
    return cell > compared;
  }
  if (operator === "neq") {
    return cell !== compared;
  }
  return cell === compared;
}

export function rowMatchesReportColumnFilter(
  row: InsideSalesSalesOrderReportRow,
  filter: InsideSalesReportColumnFilter,
): boolean {
  if (!reportColumnFilterIsActive(filter)) {
    return true;
  }
  const operator = coerceReportFilterOperator(filter.column, filter.operator);
  const kind = reportFilterColumnKind(filter.column);
  if (kind === "number") {
    return matchesNumberFilter(numberCell(row, filter.column), operator, filter.value, filter.valueTo);
  }
  if (kind === "date") {
    return matchesDateFilter(isoDate(textCell(row, filter.column)), operator, filter.value, filter.valueTo);
  }
  return matchesTextFilter(textCell(row, filter.column), operator, filter.value);
}

export function filterInsideSalesSalesOrderReportRows(
  rows: InsideSalesSalesOrderReportRow[],
  search: string,
  columnFilters: InsideSalesReportColumnFilter[] = [],
): InsideSalesSalesOrderReportRow[] {
  const needle = search.trim().toLowerCase();
  const activeFilters = columnFilters.filter(reportColumnFilterIsActive);
  return rows.filter((row) => {
    if (salesOrderStatusLabel(row.status) === "Cancelled") {
      return false;
    }
    if (needle !== "" && !haystack(row).includes(needle)) {
      return false;
    }
    return activeFilters.every((filter) => rowMatchesReportColumnFilter(row, filter));
  });
}

export function compareInsideSalesSalesOrderReportRows(
  left: InsideSalesSalesOrderReportRow,
  right: InsideSalesSalesOrderReportRow,
  sortKey: InsideSalesSalesOrderReportSortKey,
  direction: "asc" | "desc",
): number {
  const sign = direction === "asc" ? 1 : -1;
  if (NUMERIC_SORT_KEYS.has(sortKey)) {
    return (Number(left[sortKey]) - Number(right[sortKey])) * sign;
  }
  const leftValue = String(left[sortKey] ?? "");
  const rightValue = String(right[sortKey] ?? "");
  return leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: "base" }) * sign;
}

export function formatReportDate(value: string | null): string | null {
  if (value == null || value.trim() === "") {
    return null;
  }
  const normalized = value.slice(0, 10);
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) {
    return value;
  }
  return `${month}/${day}/${year}`;
}

export function insideSalesReportSalesOrderNumberClassName(status: string | null): string {
  if (isSalesOrderHubPendingStatus(status)) {
    return "font-medium text-amber-700 hover:text-amber-800";
  }
  return "font-medium text-emerald-700 hover:text-emerald-800";
}

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value == null) {
    return "";
  }
  const stringified = String(value);
  if (/[",\n\r]/.test(stringified)) {
    return `"${stringified.replace(/"/g, '""')}"`;
  }
  return stringified;
}

function csvValueForColumn(
  row: InsideSalesSalesOrderReportRow,
  column: InsideSalesSalesOrderReportSortKey,
): string {
  const kind = reportFilterColumnKind(column);
  if (kind === "date") {
    return formatReportDate(textCell(row, column)) ?? "";
  }
  if (kind === "number") {
    const amount = numberCell(row, column);
    if (amount == null) {
      return "";
    }
    if (column === "unitPrice" || column === "lineValue") {
      return amount.toFixed(2);
    }
    return String(amount);
  }
  return textCell(row, column);
}

export function buildInsideSalesSalesOrderReportCsv(rows: InsideSalesSalesOrderReportRow[]): string {
  const header = INSIDE_SALES_REPORT_FILTER_COLUMNS.map((column) => column.label).join(",");
  const body = rows.map((row) =>
    INSIDE_SALES_REPORT_FILTER_COLUMNS.map((column) => escapeCsvCell(csvValueForColumn(row, column.key))).join(","),
  );
  return ["\uFEFF" + header, ...body].join("\r\n");
}

export function insideSalesSalesOrderReportCsvFilename(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `inside-sales-report-${year}-${month}-${day}.csv`;
}

export function downloadInsideSalesSalesOrderReportCsv(rows: InsideSalesSalesOrderReportRow[]): void {
  const csv = buildInsideSalesSalesOrderReportCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = insideSalesSalesOrderReportCsvFilename();
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}
